import { ComponentType, useMemo } from 'react';
import { Box, ListItemButton } from '@mui/material';
import {
  ScheduleTask,
  SortedToPlanListBaseProps,
  TaskListCategory
} from '../types';
import { ScheduleContextMenu } from 'components/roster-portal/activity-planning/components';
import dayjs from 'dayjs';
import { PRIMARY_LIGHT, TEXT } from 'constants/colors';
import TaskListAllDone from './TaskListAllDone';
import { scrollToRoute } from 'components/roster-portal/activity-planning/utils';
import { ContextMenuOptions } from 'components/roster-portal/activity-planning/components/ContextMenu/types';
import { EventResponse } from 'generated/index';
import { PlanningEntity } from 'components/roster-portal/activity-planning/types';
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';

type TaskListContentProps = {
  category: TaskListCategory;
  scheduleToAssign: ScheduleTask[];
  scheduleWithMultipleDuties: ScheduleTask[];
  activeEntityContainingSelection: string | null;
  onChangeActiveEntity: (entityId: string) => void;
  onChangeSelection: (selectionIds: string[]) => void;
  hasActivityPlanningEditAccess: boolean;
  onChangeMousePosition: (position: { x: number; y: number }) => void;
  planEventsPerEntity: {
    planningEntityId: string;
    planEvents: EventResponse[];
    planningEntity: PlanningEntity;
  }[];
  onRouteClick?: (date: Date, routeId?: string) => void;
  isTimeline?: boolean;
  SortedToPlanListComponent: ComponentType<SortedToPlanListBaseProps>;
};

// Constant — defined outside the component so it never changes identity
const hideOptions = [
  ContextMenuOptions.CONNECT_ROUTE,
  ContextMenuOptions.SHOW_ROUTE_ON_MAP,
  ContextMenuOptions.EDIT_ROUTE_START,
  ContextMenuOptions.CHANGE_TO_CAR,
  ContextMenuOptions.CHANGE_TO_BICYCLE
];

export const TaskListContent = (props: TaskListContentProps) => {
  const {
    category,
    scheduleToAssign,
    scheduleWithMultipleDuties,
    activeEntityContainingSelection,
    onChangeActiveEntity,
    onChangeSelection,
    hasActivityPlanningEditAccess,
    onChangeMousePosition,
    planEventsPerEntity,
    onRouteClick,
    isTimeline = false,
    SortedToPlanListComponent
  } = props;

  // Hooks must be called unconditionally at the top level
  const dateNavigationPeriod = useDateNavigationStore(({ period }) => period);

  const taskListItems = useMemo(() => {
    return scheduleToAssign.map((task, index) => {
      return (
        <Box key={index + (task?.data?.planningEntityId ?? '')}>
          <ListItemButton
            onClick={() => {
              const startTime = task?.data?.start;
              const routeId = task?.data?.planningEntityId;
              if (isTimeline && onRouteClick && startTime) {
                onRouteClick(new Date(startTime), routeId);
              } else {
                scrollToRoute(task?.data?.appointmentGrouping?.entityId ?? '');
              }
            }}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              bgcolor: PRIMARY_LIGHT,
              alignItems: 'center',
              py: 1,
              pl: 2,
              pr: 0,
              borderRadius: 0.25,
              boxShadow: '0px 2px 1px #CBEFED',
              '&:hover': {
                boxShadow: '0px 2px 1px #e8ebeb'
              }
            }}
          >
            <Box display="flex" gap={4} alignItems="center">
              <Box
                fontSize={12}
                color={TEXT}
                display="flex"
                flexDirection="column"
              >
                {dateNavigationPeriod != 'day' && (
                  <Box textTransform="capitalize">
                    {dayjs(task?.data?.start).format('dddd')}
                  </Box>
                )}
                <Box>
                  {dayjs(task?.data?.start).format('HH:mm')} -{' '}
                  {dayjs(task?.data?.end).format('HH:mm')}
                </Box>
              </Box>
              <Box fontSize={14} color={TEXT}>
                {task?.data?.name}
              </Box>
            </Box>
            <ScheduleContextMenu
              appointmentGroupingEntityId={
                task?.data?.appointmentGrouping?.entityId ?? ''
              }
              appointmentGroupingStart={task?.data?.start ?? ''}
              appointmentGroupingName={task?.data?.name}
              events={task?.data?.events}
              employeeId={null}
              isAssignableToEmployee={
                task?.data?.isAssignableToEmployee ?? false
              }
              scheduleId={task?.data?.planningEntityId ?? ''}
              optionsToHide={hideOptions}
            />
          </ListItemButton>
        </Box>
      );
    });
  }, [scheduleToAssign, dateNavigationPeriod, isTimeline, onRouteClick]);

  const scheduleWithMultipleDutiesItems = useMemo(() => {
    return scheduleWithMultipleDuties.map((task, index) => {
      return (
        <Box key={index + (task?.data?.planningEntityId ?? '')}>
          <ListItemButton
            onClick={() => {
              const startTime = task?.data?.start;
              const routeId = task?.data?.planningEntityId;
              if (isTimeline && onRouteClick && startTime) {
                onRouteClick(new Date(startTime), routeId);
              } else {
                scrollToRoute(task?.data?.appointmentGrouping?.entityId ?? '');
              }
            }}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              bgcolor: PRIMARY_LIGHT,
              alignItems: 'center',
              py: 1,
              pl: 2,
              pr: 0,
              borderRadius: 0.25,
              boxShadow: '0px 2px 1px #CBEFED',
              '&:hover': {
                boxShadow: '0px 2px 1px #e8ebeb'
              }
            }}
          >
            <Box display="flex" gap={4} alignItems="center">
              <Box
                fontSize={12}
                color={TEXT}
                display="flex"
                flexDirection="column"
              >
                {dateNavigationPeriod != 'day' && (
                  <Box textTransform="capitalize">
                    {dayjs(task?.data?.start).format('dddd')}
                  </Box>
                )}
                <Box>
                  {dayjs(task?.data?.start).format('HH:mm')} -{' '}
                  {dayjs(task?.data?.end).format('HH:mm')}
                </Box>
              </Box>
              <Box fontSize={14} color={TEXT}>
                {task?.data?.name}
              </Box>
            </Box>
            <ScheduleContextMenu
              appointmentGroupingEntityId={
                task?.data?.appointmentGrouping?.entityId ?? ''
              }
              appointmentGroupingStart={task?.data?.start ?? ''}
              appointmentGroupingName={task?.data?.name}
              events={task?.data?.events}
              employeeId={task?.data?.employee?.id ?? null}
              isAssignableToEmployee={
                task?.data?.isAssignableToEmployee ?? false
              }
              scheduleId={task?.data?.planningEntityId ?? ''}
              optionsToHide={hideOptions}
            />
          </ListItemButton>
        </Box>
      );
    });
  }, [
    scheduleWithMultipleDuties,
    dateNavigationPeriod,
    isTimeline,
    onRouteClick
  ]);

  if (category === TaskListCategory.TO_PLAN_APPOINTMENTS) {
    const hasEvents = planEventsPerEntity.some(
      ({ planEvents }) => planEvents.length > 0
    );
    const multiplePlanningEntitiesSelected = planEventsPerEntity.length > 1;

    return hasEvents ? (
      <>
        {planEventsPerEntity.map(
          ({ planningEntityId, planEvents, planningEntity }) =>
            planEvents.length > 0 && (
              <SortedToPlanListComponent
                key={planningEntityId}
                activeEntityContainingSelection={
                  activeEntityContainingSelection
                }
                planningEntityId={planningEntityId}
                planningEntityType={planningEntity.type}
                readOnly={!hasActivityPlanningEditAccess}
                events={planEvents ?? []}
                onChangeActiveEntity={onChangeActiveEntity}
                onChangeSelection={onChangeSelection}
                onChangeMousePosition={onChangeMousePosition}
                multiplePlanningEntitiesSelected={
                  multiplePlanningEntitiesSelected
                }
              />
            )
        )}
      </>
    ) : (
      <TaskListAllDone />
    );
  }

  if (category === TaskListCategory.SCHEDULE_TO_ASSIGN) {
    if (scheduleToAssign.length > 0) {
      return (
        <Box display="flex" flexDirection="column" gap={1.5} paddingBottom={2}>
          {taskListItems}
        </Box>
      );
    } else {
      return <TaskListAllDone />;
    }
  }

  if (category === TaskListCategory.SCHEDULE_WITH_MULTIPLE_DUTIES) {
    if (scheduleWithMultipleDuties.length > 0) {
      return (
        <Box display="flex" flexDirection="column" gap={1.5} paddingBottom={2}>
          {scheduleWithMultipleDutiesItems}
        </Box>
      );
    } else {
      return <TaskListAllDone />;
    }
  }

  return null;
};
